import type InvoiceModel from 'src/models/InvoiceModel';
import React from 'react';

import { observer } from 'mobx-react';

import { defineMessages } from 'src/i18n';
import { useISIntl } from 'src/i18n/utils';
import { AppStore } from 'src/stores/AppStore';
import currentDocStore from '../../stores/CurrentDocumentStore';
import ColorSelect from '../ColorSelect/ColorSelect';
import { WhenLegacyPdfDownload } from '../conditionalRenderer';
import { SettingsCurrencySelector } from '../Currency';
import { RequestClientSignature } from '../RequestClientSignature';
import { Desktop } from '../Responsive';
import { DiscountSettings } from './DiscountSettings';
import { InvoicePayments } from './InvoicePayments';
import InvoicePDFButton from './InvoicePDFButton';
import { InvoiceSendReminder } from './InvoiceSendReminder';
import SendToSelfEmail from './SendToSelfEmail';
import { SignedEstimateMakeInvoice } from './SignedEstimateMakeInvoice';
import { TaxSettings } from './TaxSettings';

const messages = defineMessages({
  customize: {
    id: 'settings.list.customize',
    defaultMessage: 'Customize'
  },
  template: {
    id: 'invoiceSettings.template',
    defaultMessage: 'Template'
  },
  color: {
    id: 'invoiceSettings.color',
    defaultMessage: 'Color'
  },
  currency: {
    id: 'invoiceSettings.currency',
    defaultMessage: 'Currency'
  },
  options: {
    id: 'invoiceSettings.options',
    defaultMessage: 'Options'
  },
  share: {
    id: 'invoiceSettings.share',
    defaultMessage: 'Share'
  },
  shareButtonLabel: {
    id: 'invoiceSettings.share.button.label',
    defaultMessage: 'Get Link'
  },
  shareButtonTitle: {
    id: 'invoiceSettings.share.button.title',
    defaultMessage: 'Get a link to your {docType} to share with your client'
  },
  printButtonLabel: {
    id: 'invoiceSettings.print.button.label',
    defaultMessage: 'Print {docType}'
  },
  printButtonTitle: {
    id: 'invoiceSettings.print.button.title',
    defaultMessage: 'Print your {docType}'
  },
  overdue: {
    id: 'invoiceSettings.share.reminder.overdue',
    defaultMessage: 'Overdue'
  }
});

export const InvoiceSettings = observer((props: { store: AppStore }) => {
  const intl = useISIntl();

  const store = props.store;
  const user = store.user;
  const invoice: InvoiceModel = store.doc;
  const isLoading = store.isLoading;
  const docType = intl.getTitleizedDocumentType(currentDocStore.currentDoc.docType);

  return (
    <div className="invoice-settings">
      {invoice.hasDueDate && +invoice.balance > 0 && <InvoiceSendReminder invoice={invoice} />}
      {invoice.isSignedEstimate && <SignedEstimateMakeInvoice invoice={invoice} store={store} />}

      <SendToSelfEmail />

      <div className="invoice-delete-hidden">
        <Desktop>
          <InvoicePayments store={store} />
        </Desktop>

        {invoice.isEstimate && <RequestClientSignature user={user} invoice={invoice} />}

        <h4>{intl.formatMessage(messages.template)}</h4>
        <ColorSelect
          isLoading={isLoading}
          value={invoice.color}
          onChange={(color: string) => {
            props.store.doc.setColor(color);
          }}
        />

        <button
          title={intl.formatMessage(messages.customize)}
          className={`btn btn-print btn-block mt-3`}
          onClick={(e) => {
            e.preventDefault();
            const params = new URLSearchParams();
            params.set('invoiceId', invoice.id || 'new-document');
            params.set('docType', invoice.docType.toString());

            const templatesUrl = `${
              process.env.REACT_APP_URL || ''
            }/templates?${params.toString()}`;

            store.location.navigateHard(templatesUrl);
          }}>
          <span>{intl.fta(messages.customize)}</span>
        </button>

        <TaxSettings invoice={invoice} isLoading={isLoading} />

        <DiscountSettings invoice={invoice} isLoading={isLoading} />

        <h4>{intl.formatMessage(messages.currency)}</h4>
        <table className="invoice-detail-about">
          <tbody>
            <tr>
              <td>
                <SettingsCurrencySelector settingList={store.user.settingList} />
              </td>
            </tr>
          </tbody>
        </table>

        <h4>{intl.formatMessage(messages.options)}</h4>
        <table className="invoice-detail-about">
          <tbody>
            <tr>
              <td>
                <button
                  disabled={isLoading}
                  title={intl.formatMessage(messages.shareButtonTitle)}
                  className="btn btn-share btn-block btn-invoice-share btn-prime"
                  onClick={() => {
                    invoice.openPublicLink();
                  }}>
                  <span>{intl.formatMessage(messages.shareButtonLabel)}</span>
                </button>

                <WhenLegacyPdfDownload>
                  <InvoicePDFButton className="btn-block" />
                </WhenLegacyPdfDownload>

                <button
                  disabled={isLoading || invoice.isNew}
                  title={intl.formatMessage(messages.printButtonTitle)}
                  className={`btn btn-print btn-block`}
                  onClick={() => {
                    invoice.print();
                  }}>
                  <span>{intl.fta(messages.printButtonLabel, { docType })}</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});
